import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Bell, AlertTriangle, Info, AlertCircle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/alerts")({
  component: AlertsPage,
  head: () => ({ meta: [{ title: "Alerts — InboxPulse" }] }),
});

function AlertsPage() {
  const { data } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () =>
      (await supabase.from("alerts").select("*").order("created_at", { ascending: false })).data ??
      [],
  });

  return (
    <div className="p-8">
      <PageHeader
        title="Alerts"
        description="Reputation drops, spam spikes, blacklist hits, webhook failures."
      />
      <HelpPanel
        title="Hilfe: Warnungen ansehen"
        what={
          <>
            Liste aller <b>aktuellen Warnungen</b>: Domain in Blacklist, Spam-Anstieg,
            Reputations-Verfall, technische Probleme.
          </>
        }
        why={
          <>
            Wer Warnungen ignoriert, wacht auf einer brennenden Domain auf. Hier kannst du jeden
            Vorfall einzeln durchgehen und abhaken.
          </>
        }
        how={[
          <>Liste durchscrollen — neueste oben, rot = dringend.</>,
          <>Auf einen Eintrag klicken: Beschreibung + empfohlene Aktion.</>,
          <>
            Wenn behoben: <b>Als gelöst markieren</b>.
          </>,
        ]}
        tips={[<>Mindestens 1x am Tag reinschauen — am besten Telegram-Push aktivieren.</>]}
        glossary={[
          {
            term: "Blacklist",
            def: "Schwarze Liste von IPs/Domains, die als Spammer markiert sind. Drauf = Mails landen sofort im Spam.",
          },
          {
            term: "Reputation",
            def: "Ruf deiner Absende-Domain bei den Mail-Anbietern. Hoch = Posteingang, niedrig = Spam.",
          },
        ]}
      />
      {!data?.length ? (
        <Card className="p-12 text-center bg-card/40 border-dashed">
          <Bell className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">All clear</p>
          <p className="text-sm text-muted-foreground">Configured alerts will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.map((a) => {
            const Icon =
              a.severity === "critical"
                ? AlertTriangle
                : a.severity === "warning"
                  ? AlertCircle
                  : Info;
            const color =
              a.severity === "critical"
                ? "text-destructive"
                : a.severity === "warning"
                  ? "text-warning"
                  : "text-muted-foreground";
            return (
              <Card key={a.id} className="p-4 bg-card/60 border-border flex items-start gap-3">
                <Icon className={`size-5 mt-0.5 ${color}`} />
                <div className="flex-1">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-sm text-muted-foreground">{a.message}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
